import { useState, useEffect } from "react";
import { Users, Plus, Search, Pencil, Trash2, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nomeCliente: "", telefoneCliente: "", enderecoCliente: "" });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/customers", {
          params: { search: search.trim() || undefined, limit: 100 },
        });
        if (!cancelled) setCustomers(res.data);
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const filtered = customers.filter((c) =>
    (c.nomeCliente || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.telefoneCliente || "").includes(search) ||
    (c.enderecoCliente || "").toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    setEditing(null);
    setForm({ nomeCliente: "", telefoneCliente: "", enderecoCliente: "" });
    setDialogOpen(true);
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      nomeCliente: customer.nomeCliente || "",
      telefoneCliente: customer.telefoneCliente || "",
      enderecoCliente: customer.enderecoCliente || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = editing
        ? await api.put(`/customers/${editing.id}`, form)
        : await api.post("/customers", form);
      setCustomers((current) => [
        response.data,
        ...current.filter((customer) => customer.id !== response.data.id),
      ].slice(0, 100));
      setDialogOpen(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      setCustomers((current) => current.filter((customer) => customer.id !== id));
      setDeleteConfirm(null);
    } catch { alert("Erro ao remover cliente"); }
  };

  // Clean phone for display
  const fmtPhone = (p) => {
    if (!p) return "-";
    return p.replace("@s.whatsapp.net", "");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up stagger-1">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Clientes
          </h1>
          <p className="text-muted-foreground mt-1">
            {customers.length} clientes exibidos
          </p>
        </div>
        <Button data-testid="add-customer-btn" onClick={openNew} className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 gap-2">
          <Plus className="w-4 h-4" /> Novo Cliente
        </Button>
      </div>

      <div className="relative max-w-md animate-fade-in-up stagger-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input data-testid="customer-search-input" placeholder="Buscar por nome, telefone ou endereco..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-card border-border h-10" />
      </div>

      <Separator className="bg-border" />

      <div className="bg-card border border-border rounded-lg overflow-hidden card-shadow animate-fade-in-up stagger-3">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary hover:bg-secondary">
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-16">ID</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Telefone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Endereco</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Acoes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={5}><div className="h-5 bg-secondary rounded animate-pulse" /></TableCell></TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  {search ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.id} className="border-b border-border hover:bg-white/[0.02] transition-colors group" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{c.id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                        {(c.nomeCliente || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-sm">{c.nomeCliente || "-"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3 h-3" /> {fmtPhone(c.telefoneCliente)}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {c.enderecoCliente ? (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate max-w-[250px]">{c.enderecoCliente}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button data-testid={`edit-customer-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openEdit(c)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-customer-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteConfirm(c)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {editing ? "Editar Cliente" : "Novo Cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Nome</Label>
              <Input data-testid="customer-name-input" value={form.nomeCliente}
                onChange={(e) => setForm({ ...form, nomeCliente: e.target.value })}
                className="bg-input border-transparent focus:border-primary h-10" placeholder="Nome do cliente" />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Telefone</Label>
              <Input data-testid="customer-phone-input" value={form.telefoneCliente}
                onChange={(e) => setForm({ ...form, telefoneCliente: e.target.value })}
                className="bg-input border-transparent focus:border-primary h-10" placeholder="559899999999" />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Endereco</Label>
              <Input data-testid="customer-address-input" value={form.enderecoCliente}
                onChange={(e) => setForm({ ...form, enderecoCliente: e.target.value })}
                className="bg-input border-transparent focus:border-primary h-10" placeholder="Rua, numero, bairro..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="customer-save-btn" onClick={handleSave}
              disabled={saving || !form.nomeCliente || !form.telefoneCliente}
              className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Confirmar Exclusao</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deseja remover <span className="text-foreground font-medium">{deleteConfirm?.nomeCliente}</span>?
          </p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button data-testid="confirm-delete-customer-btn" variant="destructive" onClick={() => handleDelete(deleteConfirm.id)}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
